/**
 * @proof/seed-data/constants — side-effect-free seed identity.
 *
 * This module holds ONLY the stable UUID scheme helpers and named seed UUID
 * constants. It deliberately does NOT import the seed corpus (the three full
 * grammar-body recipes in `./index.ts`). That separation is load-bearing for
 * the web bundle: `apps/web` needs the napoletana seed UUID for the onboarding
 * "Open a worked example" entry, but must NOT pull three markdown recipe bodies
 * into the client bundle to get it (bundle-size review finding, 2026-07-02).
 * Web consumers import from `@proof/seed-data/constants`; server consumers
 * (Wrangler, no bundle-size pressure) import from the package index, which
 * re-exports everything here.
 *
 * Every value here is derived purely from the ordinal scheme — no corpus lookup
 * (`SEED_RECIPES[0].uuid`) — so importing this module cannot drag the corpus in.
 * The seed-data test suite pins these against the corpus so the two can never
 * drift.
 */

/**
 * Stable owner UUID used for all seed recipes in the D1 `public_recipes_index`.
 * Deliberately matches no row in the `user` table — seed recipes are not
 * user-owned; this value is only ever a D1 column value / seed-detection key.
 * Must not change after first deploy. The API bootstrap
 * (`apps/api/src/bootstrap/seed-public-recipes.ts`) imports this so the owner
 * UUID has exactly one definition.
 */
export const SEED_OWNER_UUID = "00000000-0000-4001-0001-000000000000";

/**
 * Derive the stable public recipe UUID for a seed at 1-based position
 * `ordinal`. Scheme: `00000000-0000-4002-0001-<12-digit ordinal>`. Defined once
 * so each corpus entry can compute its concrete `uuid` from its position.
 */
export function seedUuidForOrdinal(ordinal: number): string {
  return `00000000-0000-4002-0001-${String(ordinal).padStart(12, "0")}`;
}

/**
 * Derive the stable promoted-attempt UUID for a seed's `public_recipes_index`
 * row from its recipe UUID. Mirrors the bootstrap seeding exactly
 * (`recipeUuid.slice(0, 32) + "01"`) so the value used as the fork-lineage
 * commit when a user adopts a seed matches the value stored in the index.
 * Stable after first deploy — do not change without a D1 migration.
 */
export function seedPromotedAttemptUuid(recipeUuid: string): string {
  return `${recipeUuid.slice(0, 32)}01`;
}

/**
 * Stable public UUID of the napoletana pizza dough seed — the onboarding
 * Beat-0 "Open a worked example" entry adopts this specific seed. Computed from
 * the ordinal scheme (position 1) WITHOUT importing the corpus, so `apps/web`
 * can reference the seed by symbol without pulling three markdown bodies into
 * the client bundle (feature plan
 * `.plan/features/2026-07-02-onboarding-guided-first-recipe.md` §T1: "seed =
 * napoletana pizza dough from `@proof/seed-data`"). The seed-data test suite
 * asserts this equals `SEED_RECIPES[0].uuid` so it can never drift from the
 * corpus's first entry.
 */
export const NAPOLETANA_PIZZA_DOUGH_SEED_UUID = seedUuidForOrdinal(1);
